import {ServiceClass} from '@/decorators';
import {AlertService, ConfirmService, LoaderService} from '@/sdk';
import {BackupApi} from '@/sdk/api-services/backup.api';
import {BackupSettings, CreateUpdateBackupSettingsRequest} from '@/sdk/models/backup';

@ServiceClass()
export class BackupService {
    public backupSettings: BackupSettings = {
        id: '',
        frequency: '',
        time: '',
        dayOfWeek: undefined,
        dayOfMonth: undefined,
        monthOfYear: undefined,
        enabled: false,
        userId: '',
        created: '',
        updated: '',
    };

    public backupApi: BackupApi = new BackupApi();
    public loaderSrv: LoaderService = new LoaderService();
    public alertSrv: AlertService = new AlertService();
    public confirmSrv: ConfirmService = new ConfirmService();

    public file: File | null = null;
    public userId: string | null = null;
    public backups: Array<any> = [];
    public restoreActions: Array<string> = [];
    public isRestoreSuccessful: boolean = false;

    public save(userId: string, settings: CreateUpdateBackupSettingsRequest) {
        this.loaderSrv.showFullScreenLoader();
        this.backupApi
            .createOrUpdateBackupSettings(userId, settings)
            .then(response => {
                this.backupSettings = {
                    ...response,
                    dayOfWeek: response.dayOfWeek?.toString(),
                    dayOfMonth: response.dayOfMonth?.toString(),
                    monthOfYear: response.monthOfYear?.toString(),
                };
                this.alertSrv.show('success', 'Backup settings saved successfully.');
            })
            .catch(error => {
                console.error(error);
                this.alertSrv.show('error', 'Failed to save backup settings.');
            })
            .finally(() => {
                this.loaderSrv.hideFullScreenLoader();
            });
    }

    public loadSettings(userId: string) {
        this.loaderSrv.showFullScreenLoader();
        this.backupApi
            .getBackupSettings(userId)
            .then(response => {
                this.backupSettings = {
                    ...response,
                    dayOfWeek: response.dayOfWeek?.toString(),
                    dayOfMonth: response.dayOfMonth?.toString(),
                    monthOfYear: response.monthOfYear?.toString(),
                };
            })
            .catch(error => {
                console.error(error);
                this.alertSrv.show('error', 'Failed to load backup settings.');
            })
            .finally(() => {
                this.loaderSrv.hideFullScreenLoader();
            });
    }

    public loadBackups(userId: string) {
        this.backupApi
            .getBackups(userId)
            .then(backups => {
                this.backups = backups;
            })
            .catch(error => {
                this.alertSrv.show('error', 'Failed to fetch backups: ' + error.message);
            });
    }

    public createBackup() {
        this.confirmSrv.open('$t.Dialogs.BackupSettings.BackupConfirmTitle', '$t.Dialogs.BackupSettings.BackupConfirmMessage').then(confirmed => {
            if (confirmed) {
                this.backupApi
                    .createBackup(this.userId!)
                    .then(() => {
                        this.alertSrv.show('success', 'Backup created successfully.');
                        this.loadBackups(this.userId!);
                    })
                    .catch(error => {
                        this.alertSrv.show('error', 'Failed to create backup: ' + error);
                    });
            }
        });
    }

    public downloadBackup(backup: any) {
        this.backupApi
            .downloadBackup(backup.id)
            .then(file => {
                const url = window.URL.createObjectURL(new Blob([file]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', backup.name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                this.alertSrv.show('success', 'Backup downloaded successfully.');
            })
            .catch(error => {
                this.alertSrv.show('error', 'Failed to download backup: ' + error);
            });
    }

    public deleteBackup(backup: any) {
        this.confirmSrv.open('$t.Dialogs.BackupSettings.DeleteBackupTitle', '$t.Dialogs.BackupSettings.DeleteBackupMessage').then(confirmed => {
            if (confirmed) {
                this.backupApi
                    .deleteBackupById(backup.id)
                    .then(() => {
                        this.alertSrv.show('success', 'Backup deleted successfully.');
                        this.loadBackups(this.userId!);
                    })
                    .catch(error => {
                        this.alertSrv.show('error', 'Failed to delete backup: ' + error);
                    });
            }
        });
    }

    public restoreBackup(file: File | null) {
        if (!file) {
            this.alertSrv.show('error', 'No backup file selected for restoration.');
            return;
        }
        this.confirmSrv.open('$t.Dialogs.BackupSettings.RestoreConfirmTitle', '$t.Dialogs.BackupSettings.RestoreConfirmMessage').then(confirmed => {
            if (confirmed) {
                this.loaderSrv.showFullScreenLoader();
                this.backupApi
                    .restoreBackup(file)
                    .then((res: any) => {
                        if (res && res.actions && res.message) {
                            this.restoreActions = [...this.restoreActions, ...res.actions];
                            this.isRestoreSuccessful = true;
                            this.alertSrv.show('success', res.message);
                        } else {
                            this.isRestoreSuccessful = false;
                            this.alertSrv.show('success', 'Backup restored successfully.');
                        }
                    })
                    .catch(error => {
                        this.isRestoreSuccessful = false;
                        this.alertSrv.show('error', 'Failed to restore backup: ' + error);
                    })
                    .finally(() => {
                        this.loaderSrv.hideFullScreenLoader();
                    });
            }
        });
    }

    public reset() {
        this.backupSettings = {
            id: '',
            frequency: '',
            time: '',
            dayOfWeek: undefined,
            dayOfMonth: undefined,
            monthOfYear: undefined,
            enabled: false,
            userId: '',
            created: '',
            updated: '',
        };
    }
}
